// NotificationsPage — full notification center at /notifications.
// Lists everything (paginated), allows mark-as-read individually + in bulk.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { useNotifications } from '../context/NotificationContext';

const CATEGORY_LABEL = {
  booking:    { label: 'Bookings',     icon: 'ph-ticket',        color: '#2B3A67' },
  payment:    { label: 'Payments',     icon: 'ph-credit-card',   color: '#10B981' },
  tournament: { label: 'Tournaments',  icon: 'ph-trophy',        color: '#F59E0B' },
  live_match: { label: 'Live Match',   icon: 'ph-cricket',       color: '#E54562' },
  auction:    { label: 'Auction',      icon: 'ph-gavel',         color: '#8B5CF6' },
  operations: { label: 'Operations',   icon: 'ph-megaphone',     color: '#6B7280' },
  shop:       { label: 'Shop',         icon: 'ph-shopping-bag',  color: '#F97316' },
};

function fullDate(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { unread, markRead, markAllRead, refresh } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');  // 'all' | 'unread' | category-key

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axiosInstance
      .get('/notifications', { params: { limit: 100 } })
      .then((r) => { if (mounted) setItems(r.data.items || []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [unread]); // re-fetch when unread changes (covers mark-all-read)

  const filtered = items.filter((n) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read_at;
    return n.category === filter;
  });

  const handleClick = async (n) => {
    if (!n.read_at) await markRead(n.id);
    if (n.deep_link) navigate(n.deep_link);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-nav" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20 pt-safe">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            data-testid="notifications-back-btn"
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          >
            <i className="ph-bold ph-arrow-left text-xl text-[#2B3A67]" />
          </button>
          <h1 className="text-xl font-extrabold text-[#2B3A67] flex-1">Notifications</h1>
          {unread > 0 && (
            <button
              data-testid="notifications-mark-all-btn"
              onClick={markAllRead}
              className="text-xs font-semibold text-[#E54562] hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="max-w-2xl mx-auto px-4 pb-3 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {[
              { key: 'all', label: 'All' },
              { key: 'unread', label: `Unread${unread ? ` · ${unread}` : ''}` },
              ...Object.entries(CATEGORY_LABEL).map(([k, v]) => ({ key: k, label: v.label })),
            ].map((f) => (
              <button
                key={f.key}
                data-testid={`notification-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${
                  filter === f.key
                    ? 'bg-[#2B3A67] text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="max-w-2xl mx-auto px-4 pt-4">
        {loading ? (
          <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div data-testid="notifications-empty" className="text-center py-20">
            <i className="ph-light ph-bell-slash text-6xl text-gray-300 mb-4 block" />
            <p className="text-gray-500 font-semibold">No notifications yet</p>
            <p className="text-gray-400 text-sm mt-1">We'll let you know when something happens.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((n) => {
              const meta = CATEGORY_LABEL[n.category] || { icon: 'ph-bell', color: '#2B3A67', label: n.category };
              return (
                <button
                  key={n.id}
                  data-testid={`notification-row-${n.id}`}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex gap-3 hover:border-gray-200 transition ${n.read_at ? '' : 'ring-1 ring-[#E54562]/20'}`}
                >
                  <div
                    className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${meta.color}15` }}
                  >
                    <i className={`ph-bold ${meta.icon}`} style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <p className="font-bold text-sm text-[#2B3A67]">{n.title}</p>
                      {!n.read_at && (
                        <span className="flex-shrink-0 w-2 h-2 rounded-full bg-[#E54562] mt-1.5" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{n.body}</p>
                    <p className="text-[11px] text-gray-400">{fullDate(n.created_at)} · {meta.label}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
